<template>
  <div class="lp-build">

    <!-- TOP BAR -->
    <div class="lp-build-bar">
      <button class="lp-back" @click="$emit('back')"><CygnusIcon name="arrow-left" class="me-1" />Landing pages</button>
      <div class="lp-bar-title">
        {{ form.title || 'Untitled page' }}
        <span class="lp-bar-status">
          <span :class="['lp-pill', `lp-pill--${page && page.status === 'published' ? 'ok' : 'draft'}`]">
            {{ page && page.status === 'published' ? 'Published' : 'Draft' }}
          </span>
          <span v-if="savedNote" class="lp-saved-note">· {{ savedNote }}</span>
        </span>
      </div>
      <div class="lp-bar-mid">
        <div class="lp-seg">
          <button :class="['lp-seg-b', { on: device === 'desktop' }]" @click="device = 'desktop'"><CygnusIcon name="window" class="me-1" />Desktop</button>
          <button :class="['lp-seg-b', { on: device === 'mobile' }]" @click="device = 'mobile'"><CygnusIcon name="mobile" class="me-1" />Mobile</button>
        </div>
        <button class="lp-btn lp-btn--sec" :disabled="busy.save" @click="save(false)">
          <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.save" />Save draft
        </button>
        <button class="lp-btn lp-btn--pri" :disabled="busy.publish" @click="publish">
          <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.publish" />{{ page && page.status === 'published' ? 'Update' : 'Publish' }} <CygnusIcon name="arrow-right" class="ms-1" />
        </button>
      </div>
    </div>

    <!-- TABS -->
    <div class="lp-tabs">
      <button :class="['lp-tab', { on: tab === 'build' }]" @click="tab = 'build'">Build</button>
      <button :class="['lp-tab', { on: tab === 'attribution' }]" @click="tab = 'attribution'; loadAttribution()">Attribution</button>
    </div>

    <div v-if="error" class="lp-alert lp-alert--danger">{{ error }}</div>

    <!-- BUILD TAB -->
    <div v-show="tab === 'build'" class="lp-build-body">

      <!-- LEFT RAIL -->
      <div class="lp-rail">

        <!-- palette + block list -->
        <section class="lp-rail-sec">
          <div class="lp-rail-lbl">Blocks · click to add</div>
          <div class="lp-palette">
            <button v-for="bt in blockTypes" :key="bt.type" type="button" class="lp-pblock" @click="addBlock(bt.type)">
              <CygnusIcon :name="bt.icon" />{{ bt.label }}
            </button>
          </div>

          <div v-if="blocks.length" class="lp-blocklist">
            <draggable v-model="blocks" item-key="_key" handle=".lp-grip" :animation="150" ghost-class="lp-ghost">
              <template #item="{ element: b, index: i }">
                <div class="lp-blockcard">
                  <div class="lp-blockcard-h">
                    <CygnusIcon name="more" class="lp-grip" title="Drag to reorder" />
                    <span class="lp-blockcard-type">{{ typeLabel(b.type) }}</span>
                    <button class="lp-icon-btn" :disabled="i === 0" @click="move(i, -1)"><CygnusIcon name="chevron-up" /></button>
                    <button class="lp-icon-btn" :disabled="i === blocks.length - 1" @click="move(i, 1)"><CygnusIcon name="chevron-down" /></button>
                    <button class="lp-icon-btn lp-icon-btn--danger" @click="removeBlock(i)"><CygnusIcon name="trash" /></button>
                  </div>
                  <!-- per-type editors -->
                  <input v-if="b.type === 'eyebrow'" class="lp-in" v-model="b.text" placeholder="Small label above the heading" />
                  <input v-else-if="b.type === 'heading'" class="lp-in" v-model="b.text" placeholder="Heading text" />
                  <textarea v-else-if="b.type === 'markdown'" class="lp-in" rows="2" v-model="b.body" placeholder="Paragraph text"></textarea>
                  <input v-else-if="b.type === 'image'" class="lp-in" v-model="b.src" placeholder="Image URL" />
                  <template v-else-if="b.type === 'button'">
                    <input class="lp-in mb-1" v-model="b.text" placeholder="Button label" />
                    <input class="lp-in" v-model="b.url" placeholder="Link URL (e.g. #form or https://…)" />
                  </template>
                  <textarea v-else-if="b.type === 'bullets'" class="lp-in" rows="3" :value="(b.items || []).join('\n')" placeholder="One bullet per line" @input="setBullets(b, $event.target.value)"></textarea>
                  <template v-else-if="b.type === 'testimonial'">
                    <textarea class="lp-in mb-1" rows="2" v-model="b.quote" placeholder="Client quote"></textarea>
                    <input class="lp-in" v-model="b.author" placeholder="Attribution (e.g. A happy client)" />
                  </template>
                  <div v-else-if="b.type === 'form'" class="lp-block-note">
                    <CygnusIcon name="list" class="me-1" />Capture form —
                    <span v-if="formDest.leadFormId">renders “{{ selectedLeadFormName }}”.</span>
                    <span v-else>choose a form under <b>Form destination</b> below.</span>
                  </div>
                  <div v-else-if="b.type === 'divider'" class="lp-block-note">Horizontal divider.</div>
                </div>
              </template>
            </draggable>
          </div>
          <p v-else class="lp-empty">No blocks yet — add one above.</p>
        </section>

        <!-- AI assist — small, contained, teal -->
        <section class="lp-rail-sec">
          <div class="lp-ai">
            <div class="lp-ai-h"><CygnusIcon name="bolt" class="me-1" />Draft copy from pipeline</div>
            <p v-if="aiAudience">
              Writes a headline and intro aimed at this page’s audience —
              <b>{{ aiAudience }}</b>. It fills the hero blocks; you review before anything’s saved.
            </p>
            <p v-else class="lp-ai-muted">
              Link a pipeline below first — the draft is written from its audience.
            </p>
            <button class="lp-btn lp-btn--ai" :disabled="!aiAudience || busy.ai" @click="draftCopy">
              <CygnusIcon :name="busy.ai ? 'spinner' : 'bolt'" :spin="busy.ai" class="me-1" />Draft copy
            </button>
            <div class="lp-ai-foot">
              <span>≈ <b>1 AI token</b> per draft</span>
              <span class="lp-dot">·</span>
              <span>fills the eyebrow, heading, intro &amp; bullets</span>
            </div>
            <p v-if="aiMessage" :class="['lp-ai-msg', aiOk ? 'ok' : 'bad']">{{ aiMessage }}</p>
          </div>
        </section>

        <!-- page settings -->
        <section class="lp-rail-sec">
          <div class="lp-rail-lbl">Page settings</div>
          <div class="lp-field">
            <label>Page title</label>
            <input class="lp-in" v-model="form.title" placeholder="The page’s name" />
          </div>
          <div class="lp-field">
            <label>URL slug</label>
            <div class="lp-slug">
              <span class="lp-slug-pre">{{ slugPrefix }}</span>
              <input class="lp-in lp-in--mono" v-model="form.slug" placeholder="page-slug" />
            </div>
          </div>
          <div class="lp-field">
            <label>SEO snippet <span class="lp-hint">shown in search &amp; link previews</span></label>
            <textarea class="lp-in" rows="2" v-model="form.meta_description" placeholder="A one-line description of the page"></textarea>
          </div>
        </section>

        <!-- form destination -->
        <section class="lp-rail-sec">
          <div class="lp-rail-lbl">Form destination</div>
          <div class="lp-field">
            <label>Capture form</label>
            <select class="lp-in" v-model="formDest.leadFormId" @change="onLeadFormChange">
              <option :value="null">— none selected —</option>
              <option v-for="f in leadForms" :key="f.id" :value="f.id">{{ f.name }}</option>
            </select>
            <p class="lp-hint mt-1">The form block posts to this capture form.</p>
          </div>
          <template v-if="leadFormDetail">
            <div class="lp-field">
              <label>Add submissions to list</label>
              <select class="lp-in" v-model="leadFormDetail.list_id">
                <option :value="null">— no list —</option>
                <option v-for="l in lists" :key="l.id" :value="l.id">{{ l.name }}</option>
              </select>
            </div>
            <div class="lp-field">
              <label>After they submit</label>
              <label class="lp-radio">
                <input type="radio" value="message" v-model="thankYouMode" />
                <span><b>Show a thank-you message</b><br><input class="lp-in mt-1" v-model="leadFormDetail.success_message" placeholder="Check your inbox — it’s on its way." /></span>
              </label>
              <label class="lp-radio">
                <input type="radio" value="redirect" v-model="thankYouMode" />
                <span><b>Redirect to a URL</b><br><input class="lp-in mt-1" v-model="leadFormDetail.redirect_url" placeholder="https://…" /></span>
              </label>
            </div>
            <div class="lp-field">
              <label class="lp-check">
                <input type="checkbox" :checked="leadFormDetail.double_optin === 1" @change="leadFormDetail.double_optin = $event.target.checked ? 1 : 0" />
                <span>Double opt-in <b>(recommended)</b></span>
              </label>
              <p class="lp-hint">Subscribers confirm by email before entering any flow. Unconfirmed submissions show as “awaiting confirmation” in attribution.</p>
            </div>
            <button class="lp-btn lp-btn--sec lp-btn--sm" :disabled="busy.form" @click="saveLeadForm">
              <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.form" />Save form destination
            </button>
            <p v-if="formMessage" :class="['lp-ai-msg', formOk ? 'ok' : 'bad']">{{ formMessage }}</p>
          </template>
        </section>

        <!-- pipelines & attribution config -->
        <section class="lp-rail-sec">
          <div class="lp-rail-lbl">Pipelines &amp; attribution</div>
          <div class="lp-field">
            <label>Link a pipeline</label>
            <div class="lp-linkrow">
              <select class="lp-in" v-model="linkForm.drip_flow_id">
                <option :value="null">— choose a pipeline —</option>
                <option v-for="p in linkablePipelines" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
              <button class="lp-btn lp-btn--sec lp-btn--sm" :disabled="!linkForm.drip_flow_id || busy.link" @click="linkPipeline">Link</button>
            </div>
          </div>

          <div v-for="lnk in links" :key="lnk.id" class="lp-link">
            <div class="lp-link-h">
              <span class="lp-link-name">{{ lnk.flow_name }}</span>
              <button class="lp-icon-btn lp-icon-btn--danger" @click="unlinkPipeline(lnk)"><CygnusIcon name="x" /></button>
            </div>
            <div class="lp-link-fields">
              <input class="lp-in lp-in--sm" v-model="lnk.match_utm_campaign" placeholder="match utm_campaign" @change="updateLink(lnk)" />
              <input class="lp-in lp-in--sm" v-model="lnk.match_utm_source" placeholder="match utm_source" @change="updateLink(lnk)" />
              <input class="lp-in lp-in--sm" v-model="lnk.match_referrer_host" placeholder="match referrer host" @change="updateLink(lnk)" />
              <label class="lp-check lp-check--sm">
                <input type="radio" name="lp-default" :checked="lnk.is_default === 1" @change="setDefault(lnk)" />
                <span>Default (catch-all)</span>
              </label>
            </div>
          </div>
          <p class="lp-hint">A submission is attributed by utm_campaign → utm_source → referrer host → default. Unmatched submissions count as <b>unattributed</b>.</p>
        </section>
      </div>

      <!-- RIGHT: LIVE PREVIEW -->
      <div class="lp-canvas">
        <div class="lp-canvas-bar">
          <span v-if="preview.loading" class="lp-live-dot" title="Updating preview…"></span>
          <span class="lp-canvas-hint">Live preview — same renderer as the published page</span>
        </div>
        <div v-if="preview.error" class="lp-alert lp-alert--warn">{{ preview.error }}</div>
        <div :class="['lp-frame-wrap', device]">
          <iframe class="lp-iframe" sandbox="" :srcdoc="preview.html"></iframe>
        </div>
      </div>
    </div>

    <!-- PUBLISH NOTE -->
    <div v-show="tab === 'build'" class="lp-build-foot">
      <span class="lp-warn"><CygnusIcon name="warning" class="me-1" /></span>
      <span class="lp-pubnote">
        Publishing pushes this live at <b class="lp-in--mono">{{ slugPrefix }}{{ form.slug }}</b> — it stays on your musubi.page subdomain for now (custom domains come later).
      </span>
    </div>

    <!-- ATTRIBUTION TAB -->
    <div v-show="tab === 'attribution'" class="lp-attr-wrap">
      <LandingPageAttribution :page-id="pageId" :api-base="apiBase" :reload-key="attrKey" />
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import { apiErrorMessage } from "./util/aiError";
import draggable from "vuedraggable";
import LandingPageAttribution from "@root/components/LandingPageAttribution.vue";

// Landing pages P1b — the split-pane builder (Screen B). Reuses the
// icarus-branding email-editor primitives at page scale: vuedraggable block
// reorder, a debounced server-rendered live preview (the SAME renderer the
// public page uses, via POST /landing-pages/preview), a settings rail, and the
// one contained teal AI moment ("Draft copy from pipeline"). Indigo = commit
// (Publish); teal = AI; Save draft is deliberately neither.
export default {
  name: "LandingPageBuilder",
  components: { draggable, LandingPageAttribution },
  props: {
    pageId: { type: Number, required: true },
    apiBase: { type: String, default: "/api/v1" },
    lists: { type: Array, default: () => [] },
    pipelines: { type: Array, default: () => [] },
    leadForms: { type: Array, default: () => [] },
    tenantSlug: { type: String, default: "" },
  },
  emits: ["back", "saved"],
  data() {
    return {
      api: useApi(),
      tab: "build",
      device: "desktop",
      page: null,
      form: { title: "", slug: "", meta_title: "", meta_description: "" },
      blocks: [],
      blockKeySeq: 0,
      blockTypes: [
        { type: "eyebrow", label: "Eyebrow", icon: "tags" },
        { type: "heading", label: "Heading", icon: "pen" },
        { type: "markdown", label: "Text", icon: "list" },
        { type: "bullets", label: "Bullets", icon: "list" },
        { type: "button", label: "Button", icon: "checkbox" },
        { type: "image", label: "Image", icon: "file" },
        { type: "testimonial", label: "Testimonial", icon: "note-sticky" },
        { type: "form", label: "Form", icon: "list" },
        { type: "divider", label: "Divider", icon: "minus" },
      ],
      preview: { html: "", loading: false, error: "" },
      previewReqId: 0,
      previewTimer: null,
      links: [],
      linkForm: { drip_flow_id: null },
      formDest: { leadFormId: null },
      leadFormDetail: null,
      thankYouMode: "message",
      attrKey: 0,
      busy: { save: false, publish: false, ai: false, link: false, form: false },
      error: null,
      savedNote: "",
      aiMessage: "",
      aiOk: false,
      formMessage: "",
      formOk: false,
    };
  },
  computed: {
    slugPrefix() {
      const t = this.tenantSlug || "your-workspace";
      return `${t}.musubi.page/`;
    },
    // The audience descriptor for the AI draft — the default link's, else the
    // first linked pipeline's.
    aiAudience() {
      const def = this.links.find((l) => l.is_default === 1) || this.links[0];
      return def ? (def.audience_descriptor || "") : "";
    },
    // The kind string fed to the AI (from the page's product tag if set).
    aiKind() {
      return this.page && this.page.product ? this.page.product : "landing page";
    },
    linkablePipelines() {
      const linkedIds = new Set(this.links.map((l) => l.drip_flow_id));
      return (this.pipelines || []).filter((p) => !linkedIds.has(p.id));
    },
    selectedLeadFormName() {
      const f = (this.leadForms || []).find((x) => x.id === this.formDest.leadFormId);
      return f ? f.name : "";
    },
  },
  watch: {
    blocks: { deep: true, handler() { this.schedulePreview(); } },
    "form.title": function () { this.schedulePreview(); },
    "form.meta_description": function () { this.schedulePreview(); },
  },
  mounted() {
    this.loadPage();
    this.loadLinks();
  },
  methods: {
    nextBlockKey() { return ++this.blockKeySeq; },
    typeLabel(t) { return (this.blockTypes.find((b) => b.type === t) || {}).label || t; },

    async loadPage() {
      this.error = null;
      try {
        const res = await this.api.get(`${this.apiBase}/admin/landing-pages/${this.pageId}`);
        const payload = res?.data ?? res;
        this.page = payload.page;
        this.form = {
          title: payload.page.title || "",
          slug: payload.page.slug || "",
          meta_title: payload.page.meta_title || "",
          meta_description: payload.page.meta_description || "",
        };
        this.blocks = (payload.page.blocks_json || []).map((b) => ({ ...b, _key: this.nextBlockKey() }));
        // Adopt the form block's form_id as the current form destination.
        const formBlock = this.blocks.find((b) => b.type === "form" && b.form_id);
        if (formBlock) {
          this.formDest.leadFormId = formBlock.form_id;
          this.loadLeadFormDetail(formBlock.form_id);
        }
        this.schedulePreview(true);
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || "Failed to load page.";
      }
    },

    plainBlocks() {
      return this.blocks.map(({ _key, ...rest }) => rest);
    },
    addBlock(type) {
      const b = { type, _key: this.nextBlockKey() };
      if (type === "eyebrow") b.text = "";
      else if (type === "heading") { b.text = ""; b.level = 2; }
      else if (type === "markdown") b.body = "";
      else if (type === "image") b.src = "";
      else if (type === "button") { b.text = ""; b.url = "#"; }
      else if (type === "bullets") b.items = [];
      else if (type === "testimonial") { b.quote = ""; b.author = ""; }
      else if (type === "form") b.form_id = this.formDest.leadFormId || 0;
      this.blocks.push(b);
    },
    removeBlock(i) { this.blocks.splice(i, 1); },
    move(i, dir) {
      const j = i + dir;
      if (j < 0 || j >= this.blocks.length) return;
      const a = this.blocks;
      [a[i], a[j]] = [a[j], a[i]];
    },
    setBullets(b, value) {
      b.items = String(value || "").split(/\r\n|\r|\n/).map((s) => s.trim()).filter(Boolean);
    },

    // ---- live preview (debounced, server-rendered) ----
    schedulePreview(immediate = false) {
      if (this.previewTimer) { clearTimeout(this.previewTimer); this.previewTimer = null; }
      if (immediate) { this.fetchPreview(); return; }
      this.previewTimer = setTimeout(() => this.fetchPreview(), 600);
    },
    async fetchPreview() {
      const reqId = ++this.previewReqId;
      this.preview.loading = true;
      try {
        const res = await this.api.post(`${this.apiBase}/admin/landing-pages/preview`, {
          blocks_json: this.plainBlocks(),
          title: this.form.title,
          meta_description: this.form.meta_description,
        });
        if (reqId !== this.previewReqId) return;
        const payload = res?.data ?? res;
        this.preview.html = payload?.html || "";
        this.preview.error = "";
      } catch (e) {
        if (reqId !== this.previewReqId) return;
        this.preview.error = "Failed to render preview.";
      } finally {
        if (reqId === this.previewReqId) this.preview.loading = false;
      }
    },

    // ---- save / publish ----
    async save(silent) {
      this.busy.save = true; this.error = null; this.savedNote = "";
      try {
        // sync the form block's form_id to the chosen destination.
        this.blocks.forEach((b) => { if (b.type === "form") b.form_id = this.formDest.leadFormId || 0; });
        const body = {
          title: this.form.title,
          slug: this.form.slug,
          meta_title: this.form.meta_title || null,
          meta_description: this.form.meta_description || null,
          blocks_json: this.plainBlocks(),
        };
        const res = await this.api.put(`${this.apiBase}/admin/landing-pages/${this.pageId}`, body);
        const payload = res?.data ?? res;
        if (payload?.page) {
          this.page = payload.page;
          this.form.slug = payload.page.slug;
          this.savedNote = "Saved just now";
          this.$emit("saved");
        } else {
          this.error = payload?.error || "Save failed.";
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || "Save failed.";
      }
      this.busy.save = false;
      return !this.error;
    },
    async publish() {
      const ok = await this.save(true);
      if (!ok) return;
      this.busy.publish = true;
      try {
        const res = await this.api.post(`${this.apiBase}/admin/landing-pages/${this.pageId}/publish`, {});
        const payload = res?.data ?? res;
        if (payload?.page) {
          this.page = payload.page;
          this.savedNote = "Published just now";
          this.$emit("saved");
        } else {
          this.error = payload?.error || "Publish failed.";
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || "Publish failed.";
      }
      this.busy.publish = false;
    },

    // ---- AI: draft copy from pipeline ----
    async draftCopy() {
      if (!this.aiAudience) return;
      this.busy.ai = true; this.aiMessage = ""; this.aiOk = false;
      try {
        const res = await this.api.post(`${this.apiBase}/admin/landing-pages/draft-copy`, {
          audience: this.aiAudience,
          kind: this.aiKind,
        });
        const payload = res?.data ?? res;
        if (payload?.headline) {
          this.applyDraft(payload);
          this.aiOk = true;
          this.aiMessage = `Draft applied — used ${payload.tokens_used || 1} AI token${(payload.tokens_used || 1) === 1 ? "" : "s"}. Review before saving.`;
        } else {
          this.aiMessage = payload?.error || "Draft failed.";
        }
      } catch (e) {
        this.aiMessage = apiErrorMessage(e, "Draft failed.");
      }
      this.busy.ai = false;
    },
    // Fill the hero blocks in place; create them if the page has none yet.
    applyDraft(d) {
      const setOrAdd = (type, patch, atTop) => {
        let b = this.blocks.find((x) => x.type === type);
        if (!b) { b = { type, _key: this.nextBlockKey() }; if (atTop) this.blocks.unshift(b); else this.blocks.push(b); }
        Object.assign(b, patch);
      };
      if (d.eyebrow) setOrAdd("eyebrow", { text: d.eyebrow }, true);
      setOrAdd("heading", { text: d.headline, level: 1 });
      setOrAdd("markdown", { body: d.subhead });
      if (Array.isArray(d.bullets) && d.bullets.length) setOrAdd("bullets", { items: d.bullets.slice() });
    },

    // ---- pipeline links ----
    async loadLinks() {
      try {
        const res = await this.api.get(`${this.apiBase}/admin/landing-pages/${this.pageId}/pipelines`);
        const payload = res?.data ?? res;
        this.links = (payload?.data || []).map((l) => ({ ...l }));
      } catch (e) { /* non-fatal */ }
    },
    async linkPipeline() {
      if (!this.linkForm.drip_flow_id) return;
      this.busy.link = true;
      try {
        const res = await this.api.post(`${this.apiBase}/admin/landing-pages/${this.pageId}/pipelines`, {
          drip_flow_id: this.linkForm.drip_flow_id,
        });
        const payload = res?.data ?? res;
        this.links = (payload?.data || []).map((l) => ({ ...l }));
        this.linkForm.drip_flow_id = null;
      } catch (e) { this.error = e?.response?.data?.error || "Failed to link pipeline."; }
      this.busy.link = false;
    },
    async updateLink(lnk) {
      try {
        const res = await this.api.post(`${this.apiBase}/admin/landing-pages/${this.pageId}/pipelines`, {
          drip_flow_id: lnk.drip_flow_id,
          match_utm_campaign: lnk.match_utm_campaign || null,
          match_utm_source: lnk.match_utm_source || null,
          match_referrer_host: lnk.match_referrer_host || null,
          is_default: lnk.is_default,
        });
        const payload = res?.data ?? res;
        this.links = (payload?.data || []).map((l) => ({ ...l }));
      } catch (e) { this.error = e?.response?.data?.error || "Failed to update link."; }
    },
    setDefault(lnk) {
      this.links.forEach((l) => { l.is_default = l.id === lnk.id ? 1 : 0; });
      // Persist the newly-defaulted link (server keeps one default via the row).
      this.updateLink({ ...lnk, is_default: 1 });
    },
    async unlinkPipeline(lnk) {
      try {
        const res = await this.api.delete(`${this.apiBase}/admin/landing-pages/${this.pageId}/pipelines/${lnk.drip_flow_id}`);
        const payload = res?.data ?? res;
        this.links = (payload?.data || []).map((l) => ({ ...l }));
      } catch (e) { this.error = e?.response?.data?.error || "Failed to unlink."; }
    },

    // ---- form destination (edits the referenced lead form) ----
    onLeadFormChange() {
      this.blocks.forEach((b) => { if (b.type === "form") b.form_id = this.formDest.leadFormId || 0; });
      if (this.formDest.leadFormId) this.loadLeadFormDetail(this.formDest.leadFormId);
      else this.leadFormDetail = null;
      this.schedulePreview();
    },
    async loadLeadFormDetail(id) {
      try {
        const res = await this.api.get(`${this.apiBase}/admin/lead-forms/${id}`);
        const payload = res?.data ?? res;
        this.leadFormDetail = payload.form;
        this.thankYouMode = this.leadFormDetail.redirect_url ? "redirect" : "message";
      } catch (e) { this.leadFormDetail = null; }
    },
    async saveLeadForm() {
      if (!this.leadFormDetail) return;
      this.busy.form = true; this.formMessage = ""; this.formOk = false;
      try {
        const body = {
          list_id: this.leadFormDetail.list_id || null,
          double_optin: this.leadFormDetail.double_optin,
          success_message: this.thankYouMode === "message" ? (this.leadFormDetail.success_message || null) : null,
          redirect_url: this.thankYouMode === "redirect" ? (this.leadFormDetail.redirect_url || null) : null,
        };
        const res = await this.api.put(`${this.apiBase}/admin/lead-forms/${this.leadFormDetail.id}`, body);
        const payload = res?.data ?? res;
        if (payload?.form) { this.leadFormDetail = payload.form; this.formOk = true; this.formMessage = "Form destination saved."; }
        else this.formMessage = payload?.error || "Save failed.";
      } catch (e) { this.formMessage = e?.response?.data?.error || "Save failed."; }
      this.busy.form = false;
    },

    // ---- attribution tab ----
    loadAttribution() { this.attrKey++; },
  },
};
</script>

<style scoped>
.lp-build { display: flex; flex-direction: column; background: var(--m-bg-content, #F5F6FA); border-radius: var(--m-radius-lg, 14px); overflow: hidden; border: 1px solid var(--m-border, #EAECF3); }
.lp-build-bar { display: flex; align-items: center; gap: 14px; padding: 11px 16px; background: var(--m-bg-card, #fff); border-bottom: 1px solid var(--m-border, #EAECF3); }
.lp-back { border: 0; background: none; font: inherit; font-weight: 600; font-size: .82rem; color: var(--m-text-secondary); cursor: pointer; padding-right: 14px; border-right: 1px solid var(--m-border); }
.lp-bar-title { font-weight: 700; font-size: .92rem; }
.lp-bar-status { font-weight: 500; }
.lp-saved-note { color: var(--m-text-tertiary); font-size: .74rem; font-weight: 500; }
.lp-bar-mid { margin-left: auto; display: flex; align-items: center; gap: 12px; }
.lp-seg { display: inline-flex; background: var(--m-bg-content); border: 1px solid var(--m-border); border-radius: 9px; padding: 3px; }
.lp-seg-b { border: 1px solid transparent; background: none; font: inherit; font-size: .76rem; font-weight: 700; color: var(--m-text-tertiary); padding: 5px 11px; border-radius: 7px; cursor: pointer; }
.lp-seg-b.on { background: #fff; color: var(--m-text-primary); border-color: var(--m-border); box-shadow: var(--m-shadow-card); }

.lp-tabs { display: flex; gap: 4px; padding: 8px 16px 0; background: var(--m-bg-card, #fff); border-bottom: 1px solid var(--m-border); }
.lp-tab { border: 0; background: none; font: inherit; font-size: .84rem; font-weight: 600; color: var(--m-text-secondary); padding: 9px 14px; cursor: pointer; border-bottom: 2px solid transparent; }
.lp-tab.on { color: var(--m-accent); border-bottom-color: var(--m-accent); }

.lp-build-body { display: grid; grid-template-columns: 360px 1fr; min-height: 640px; }
.lp-rail { background: var(--m-bg-card, #fff); border-right: 1px solid var(--m-border); overflow: auto; max-height: 78vh; }
.lp-rail-sec { padding: 16px; border-bottom: 1px solid var(--m-border); }
.lp-rail-lbl { font-size: .64rem; font-weight: 800; letter-spacing: .09em; text-transform: uppercase; color: var(--m-text-tertiary); margin-bottom: 11px; }
.lp-palette { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; margin-bottom: 12px; }
.lp-pblock { display: flex; align-items: center; gap: 5px; border: 1px solid var(--m-border); border-radius: 8px; padding: 7px 8px; background: #fff; cursor: pointer; font-size: .72rem; font-weight: 600; color: var(--m-text-secondary); }
.lp-pblock:hover { border-color: var(--m-border-strong); color: var(--m-text-primary); background: var(--m-bg-content); }

.lp-blocklist { display: flex; flex-direction: column; gap: 8px; }
.lp-blockcard { border: 1px solid var(--m-border); border-radius: 8px; padding: 8px; background: #fff; }
.lp-blockcard-h { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.lp-blockcard-type { font-size: .74rem; font-weight: 700; color: var(--m-text-primary); }
.lp-blockcard-h .lp-icon-btn:nth-of-type(1) { margin-left: auto; }
.lp-grip { cursor: grab; color: var(--m-text-tertiary); }
.lp-ghost { opacity: .4; }
.lp-icon-btn { border: 1px solid var(--m-border); background: #fff; border-radius: 6px; width: 24px; height: 24px; font-size: .7rem; color: var(--m-text-secondary); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.lp-icon-btn:disabled { opacity: .4; cursor: default; }
.lp-icon-btn--danger:hover { color: var(--m-status-danger); border-color: var(--m-status-danger); }
.lp-block-note { font-size: .74rem; color: var(--m-text-tertiary); font-style: italic; }
.lp-empty { font-size: .78rem; color: var(--m-text-tertiary); }

.lp-in { width: 100%; border: 1px solid var(--m-border); border-radius: 8px; padding: 8px 10px; font: inherit; font-size: .82rem; background: #fff; color: var(--m-text-primary); }
.lp-in:focus { outline: none; border-color: var(--m-accent); }
.lp-in--mono { font-family: var(--m-font-mono); font-size: .76rem; }
.lp-in--sm { padding: 6px 8px; font-size: .76rem; }
.mb-1 { margin-bottom: 4px; } .mt-1 { margin-top: 4px; }

.lp-field { margin-bottom: 12px; }
.lp-field > label { display: block; font-size: .7rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: var(--m-text-secondary); margin-bottom: 5px; }
.lp-hint { font-weight: 500; text-transform: none; letter-spacing: 0; color: var(--m-text-tertiary); font-size: .72rem; }
.lp-slug { display: flex; align-items: center; border: 1px solid var(--m-border); border-radius: 8px; overflow: hidden; }
.lp-slug-pre { padding: 0 0 0 10px; font-family: var(--m-font-mono); font-size: .74rem; color: var(--m-text-tertiary); white-space: nowrap; }
.lp-slug .lp-in { border: 0; border-radius: 0; }
.lp-radio { display: flex; gap: 8px; align-items: flex-start; padding: 8px; border: 1px solid var(--m-border); border-radius: 8px; margin-bottom: 6px; font-size: .78rem; cursor: pointer; }
.lp-radio input { margin-top: 3px; }
.lp-check { display: flex; gap: 8px; align-items: center; font-size: .82rem; }
.lp-check--sm { font-size: .74rem; }

.lp-ai { background: #E9FBF8; border: 1px solid #B8EDE5; border-radius: 10px; padding: 12px; }
.lp-ai-h { display: flex; align-items: center; font-size: .8rem; font-weight: 700; color: #0F766E; margin-bottom: 6px; }
.lp-ai p { font-size: .75rem; color: var(--m-text-secondary); line-height: 1.5; margin: 0 0 10px; }
.lp-ai-muted { color: var(--m-text-tertiary) !important; }
.lp-ai-foot { display: flex; align-items: center; gap: 8px; margin-top: 9px; font-size: .68rem; color: var(--m-text-tertiary); }
.lp-dot { opacity: .5; }
.lp-ai-msg { font-size: .74rem; margin-top: 8px; }
.lp-ai-msg.ok { color: #0F766E; } .lp-ai-msg.bad { color: var(--m-status-danger); }

.lp-linkrow { display: flex; gap: 6px; }
.lp-link { border: 1px solid var(--m-border); border-radius: 8px; padding: 8px; margin-bottom: 8px; background: var(--m-bg-content); }
.lp-link-h { display: flex; align-items: center; margin-bottom: 6px; }
.lp-link-name { font-size: .78rem; font-weight: 700; }
.lp-link-h .lp-icon-btn { margin-left: auto; }
.lp-link-fields { display: flex; flex-direction: column; gap: 5px; }

.lp-canvas { padding: 20px; overflow: auto; display: flex; flex-direction: column; align-items: center; max-height: 78vh; }
.lp-canvas-bar { align-self: stretch; display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.lp-canvas-hint { font-size: .72rem; color: var(--m-text-tertiary); margin-left: auto; }
.lp-live-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--m-accent); animation: lp-pulse 1s infinite ease-in-out; }
@keyframes lp-pulse { 0%,100% { opacity: .25; } 50% { opacity: 1; } }
.lp-frame-wrap { width: 100%; }
.lp-frame-wrap .lp-iframe { width: 100%; height: 640px; background: #fff; border: 1px solid var(--m-border); border-radius: 12px; box-shadow: var(--m-shadow-panel); display: block; }
.lp-frame-wrap.desktop .lp-iframe { max-width: 720px; margin: 0 auto; }
.lp-frame-wrap.mobile .lp-iframe { width: 390px; max-width: 100%; margin: 0 auto; }

.lp-build-foot { display: flex; align-items: center; gap: 10px; padding: 11px 16px; background: var(--m-bg-card, #fff); border-top: 1px solid var(--m-border); }
.lp-warn { color: var(--m-status-warning); }
.lp-pubnote { font-size: .75rem; color: var(--m-text-tertiary); }
.lp-pubnote b { color: var(--m-text-secondary); font-weight: 600; }

.lp-attr-wrap { padding: 20px; background: var(--m-bg-content, #F5F6FA); }

.lp-btn { display: inline-flex; align-items: center; font: inherit; font-size: .82rem; font-weight: 600; border-radius: 8px; padding: 8px 14px; cursor: pointer; border: 1px solid transparent; }
.lp-btn--sm { padding: 6px 11px; font-size: .76rem; }
.lp-btn--pri { background: var(--m-accent); color: #fff; }
.lp-btn--pri:hover { background: var(--m-accent-hover); }
.lp-btn--sec { background: #fff; color: var(--m-text-secondary); border-color: var(--m-border); }
.lp-btn--sec:hover { border-color: var(--m-border-strong); color: var(--m-text-primary); }
.lp-btn--ai { background: #0F766E; color: #fff; }
.lp-btn--ai:hover { background: #14B8A6; }
.lp-btn:disabled { opacity: .55; cursor: default; }

.lp-pill { display: inline-flex; align-items: center; font-size: .64rem; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; border-radius: 999px; padding: 2px 8px; }
.lp-pill--ok { background: #E6F6F1; color: #0D9488; }
.lp-pill--draft { background: #F1F5F9; color: var(--m-text-tertiary); }

.lp-alert { padding: 8px 12px; border-radius: 8px; font-size: .8rem; margin: 10px 16px; }
.lp-alert--danger { background: #FCEDED; color: var(--m-status-danger); }
.lp-alert--warn { background: #FDF3E4; color: var(--m-status-warning); }

@media (max-width: 900px) { .lp-build-body { grid-template-columns: 1fr; } }
</style>
